import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Api from '../utils/Api';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/**
 * ActivateCardModal — 学员卡"开卡"确认 modal (v0.7.6, 辉哥 10/2 23:06 拍板)
 *
 * 触发场景: 学员列表 card.is_activated=false 时显示"开卡"按钮 (代替"延期"按钮)
 *
 * 交互:
 *   1. 标题: "开卡" + 📅 图标 (绿色 #10b981)
 *   2. 卡片信息 (template_name + 总课时 + 剩余课时) 静态展示
 *   3. 确认提示文案: "确认开卡并设置过期时间为 YYYY-MM-DD 吗?"
 *   4. 过期时间 flatpickr (跟 BindCardModal 同款 ZS-flatpickr-input, 默认 +30 天)
 *   5. [取消] [确认开卡] button (主按钮绿色)
 *
 * 提交: Api.activate('student-cards', card.id, { expires_at })
 *
 * v0.17.33 — 撤掉"经办人"选择器 (辉哥 10/14 14:17 原话「绑卡的时候需要选择经办人,
 *   开卡的时候就不用再选择了」)。**经办人是绑卡时定下来的归属**, 开卡只应做
 *   "未激活 → 已激活 + 设过期时间" 这一件事:
 *   - 之前开卡能改经办人, 等于给了一次静默改业绩归属的机会。业绩报表 (消课统计
 *     「月度业绩」/ 排行 / 销售跟进) 查的就是 agent_id + agent_type, 改了就对不上
 *     绑卡时定下的账, 且没有任何留痕。
 *   - 本次只改前端: ActivateStudentCardController L74-75 的 legacy 分支本来就
 *     「attrs 里没有 agent_id / agent_type 就不动经办人」, 所以前端不再传这两个
 *     key, 卡上的经办人就原样保留。后端那个宽容分支留给其它调用方, 没动。
 *   - 附带修好: canSubmit 之前要求 agent_id + agent_type 必填, 导致历史卡
 *     (agent_id IS NULL) 连"延期过期时间"之外的开卡操作都被挡住、根本开不了卡。
 *     现在只校验 expires_at, 历史卡可以正常开卡。
 *   - loadAgents() 保留: 只读那行要显示经办人姓名, 而 ListStudentCardsController
 *     只返回 agent_id / agent_type, 不返回 display_name / username (后端不改),
 *     所以还得靠这份用户列表做 id → 姓名 的解析。它不再喂任何 <select>。
 *
 * 注意: 卡已激活 (is_activated=true) 时, 后端拒绝 — 不应走到这个 modal
 */
export default class ActivateCardModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.card = vnode.attrs.card;
    this.student = vnode.attrs.student;
    this.onCloseCb = vnode.attrs.onClose;

    // 默认过期时间 = +30 天 (跟常见"月卡"开卡默认值一致)
    const now = new Date();
    now.setDate(now.getDate() + 30);
    const pad = n => String(n).padStart(2, '0');
    const defaultExpires = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    this.form = {
      expires_at: this.card && this.card.expires_at ? this.card.expires_at : defaultExpires
    };
    // v0.17.33 — this.agents 不再喂任何 <select> (经办人选择器已删), 只用来把
    //   卡上的 agent_id 解析成姓名, 给下面那行只读展示用。
    this.agents = [];
    // ⚠️ loading 仍然有读者: 只读那行在列表回来之前显示"加载中…"。v0.7.8d 时
    //   它唯一的读者是已删的"当前校区暂无销售或教练"红字提示; 若把 loadAgents()
    //   一起删掉却留着 this.loading, 它会变成只写状态 (modal 卡 loading 空白)。
    this.loading = true;
    this.saving = false;
    this.error = '';
    this._flatpickrTimer = null;
    this._flatpickrInstance = null;
    this.loadAgents();
  }
  className() {
    return 'Modal--small Modal--fullscreen-padded ZS-ActivateCardModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-calendar-check",
      style: "color:var(--zs-success-10b981, #10b981);margin-right:8px;"
    }), "\u5F00\u5361");
  }
  async oncreate(vnode) {
    super.oncreate(vnode);
    this.tryInitFlatpickr();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.tryInitFlatpickr();
  }

  // v0.17.33 — 保留, 但用途收窄: v0.7.8d 是给"经办人" <select> 拉候选, 现在只
  //   用来把卡上的 agent_id 解析成 display_name / username 做只读展示。
  //   后端不改 ⇒ ListStudentCardsController 不返回经办人姓名, 这份列表是唯一来源。
  async loadAgents() {
    try {
      const cardSchoolId = this.card && this.card.school_id ? Number(this.card.school_id) : null;
      const a = await Api.list('users', {
        // v0.17.38「A 方案」— 去掉 manager。这份列表会**写进 student_cards.agent_type**
        //   (激活卡时提交), 而 agent_type 只表达「业绩归属」sales / coach;
        //   店长是正交标记 (RoleResolver::isManager), 在员工分配弹窗里选, 不在这张卡里选。
        //   ⚠️ 只改**会写 agent_type** 的表单 —— AssignStaffModal / LessonEditorModal /
        //      SalesFollowupCreateModal 的 'coach,sales,manager' **保持原样**。
        role: 'coach,sales',
        ...(cardSchoolId ? {
          school_id: cardSchoolId
        } : {})
      });
      this.agents = a || [];
    } catch (e) {
      console.error('activate card load agents failed', e);
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this._flatpickrTimer) {
      clearTimeout(this._flatpickrTimer);
      this._flatpickrTimer = null;
    }
    if (this._flatpickrInstance) {
      this._flatpickrInstance.destroy();
      this._flatpickrInstance = null;
    }
  }

  // r.1 FAIL MINOR 3: vendor flatpickr 异步加载, 等 ready (跟 BindCardModal L161-189 同款)
  tryInitFlatpickr() {
    if (this._flatpickrInstance) return;
    const tryOnce = () => {
      if (typeof window.flatpickr !== 'function') {
        this._flatpickrTimer = setTimeout(tryOnce, 100);
        return;
      }
      const el = this.element && this.element.querySelector('.ZS-flatpickr-input');
      if (!el) return;
      try {
        this._flatpickrInstance = window.flatpickr(el, {
          dateFormat: 'Y-m-d',
          minDate: 'today',
          locale: window.flatpickr.l10ns && window.flatpickr.l10ns.zh || 'default',
          onChange: (selectedDates, dateStr) => {
            this.form.expires_at = dateStr;
            m.redraw();
          }
        });
        if (this.form.expires_at) {
          this._flatpickrInstance.setDate(this.form.expires_at, false);
        }
      } catch (e) {
        console.error('activate card flatpickr init failed', e);
      }
    };
    tryOnce();
  }
  content() {
    const card = this.card || {};
    const studentName = this.student ? this.student.display_name || this.student.username : card.student_display_name || '学员';
    return m("div", {
      className: "ZS-ActivateCardModal-body"
    }, m("div", {
      className: "ZS-ActivateCardModal-cardinfo"
    }, m("div", {
      className: "ZS-ActivateCardModal-cardname"
    }, m("i", {
      className: "fas fa-id-card",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:8px;"
    }), card.template_name || `学员卡 #${card.id}`), m("div", {
      className: "ZS-ActivateCardModal-stats"
    }, m("span", null, "\u5B66\u5458: ", m("strong", null, studentName)), m("span", null, "\u603B\u8BFE\u65F6: ", m("strong", null, card.total_lessons)), m("span", null, "\u5269\u4F59: ", m("strong", null, card.remaining_lessons)))), m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: "fas fa-user-tie",
      style: "color:var(--zs-brand-3b82f6, #3b82f6);margin-right:6px;"
    }), "\u7ECF\u529E\u4EBA"), m("div", {
      className: "helpText"
    }, this.agentText())), m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: "fas fa-calendar-days",
      "aria-hidden": "true"
    }), " \u8FC7\u671F\u65F6\u95F4"), m("input", {
      className: "FormControl ZS-flatpickr-input",
      type: "text",
      placeholder: "YYYY-MM-DD",
      value: this.form.expires_at,
      readonly: true
    }), m("div", {
      className: "helpText"
    }, "\u5361\u5C06\u5728\u6B64\u65E5\u671F\u81EA\u52A8\u5931\u6548\u3002\u6FC0\u6D3B\u540E\u53EF\u901A\u8FC7\"\u5EF6\u671F\"\u4FEE\u6539\u3002")), m("div", {
      className: "ZS-ActivateCardModal-hint"
    }, m("i", {
      className: "fas fa-info-circle",
      style: "color:var(--zs-success-10b981, #10b981);margin-right:6px;"
    }), "\u786E\u8BA4\u5C06\u8FD9\u5F20\u5361\u6807\u8BB0\u4E3A", m("b", null, "\u5DF2\u5F00\u5361"), "\u5E76\u8BBE\u7F6E\u8FC7\u671F\u65F6\u95F4\u4E3A ", m("b", null, this.form.expires_at), " \u5417?", m("br", null), "\u5F00\u5361\u540E\u8BE5\u5361\u5C06\u53EF\u4EE5\u6B63\u5E38\u7528\u4E8E\u7EA6\u8BFE\u3002"), this.error && m("div", {
      className: "ZS-ActivateCardModal-error"
    }, this.error), m("div", {
      className: "Modal-footer"
    }, m(Button, {
      className: "Button Button--secondary",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m(Button, {
      className: "Button Button--primary ZS-ActivateCardModal-submit",
      disabled: this.saving || !this.canSubmit(),
      loading: this.saving,
      onclick: () => this.submit()
    }, "\u786E\u8BA4\u5F00\u5361")));
  }

  // v0.17.33 — 经办人只读文案 (角色三元照抄 v0.7.8d 那段 <option> 里的写法,
  //   但角色取**卡上存的** card.agent_type 而不是用户列表的 role — 业绩归属看的是这个字段)。
  //   ⚠️ 这只是展示, 没有任何写入路径; 要真改经办人请走绑卡 / 编辑卡。
  agentText() {
    const card = this.card || {};
    if (!card.agent_id) return '未设置';
    // 列表还没回来时先别下结论, 避免把有经办人的卡闪一下"未设置"
    if (this.loading) return '加载中…';
    // v0.17.38「A 方案」— 'manager' 分支新数据永远走不到 (agent_type 只可能是 sales / coach)。
    //   **刻意保留**做存量兼容: migration 之前的脏数据若仍被读到, 至少显示成「店长」。
    const roleLabel = card.agent_type === 'coach' ? '教练' : card.agent_type === 'manager' ? '店长' : '销售';
    const matched = (this.agents || []).find(u => Number(u.id) === Number(card.agent_id));
    // 匹配不到 (经办人已离职 / 不在本校区 / role 不在那三类里) 时退回 #id,
    //   绝不显示"未设置" — 那会把"有归属"误报成"没归属"。
    if (!matched) return `#${card.agent_id}（${roleLabel}）`;
    return `${matched.display_name || matched.username}（${roleLabel}）`;
  }

  // v0.17.33 — 去掉 agent_id / agent_type 必填校验。
  //   之前这两项必填 ⇒ 历史卡 (agent_id IS NULL) 的"确认开卡"按钮永远 disabled,
  //   卡根本开不了 (连带过期时间也设不了)。开卡现在只校验过期时间。
  canSubmit() {
    return !this.saving && !!this.form.expires_at;
  }
  async submit() {
    if (!this.canSubmit()) return;
    this.saving = true;
    this.error = '';
    m.redraw();
    try {
      await Api.activate('student-cards', this.card.id, {
        expires_at: this.form.expires_at
        // v0.17.33 — 不再发 agent_id / agent_type: 开卡只改状态 + 过期时间。
        //   不发这两个 key ⇒ 走后端 legacy 分支 (ActivateStudentCardController
        //   L74-75), 卡上的经办人原样保留, 业绩归属不会被静默篡改。
      });
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      const msg = errReason(e, '开卡失败');
      this.error = msg;
      this.saving = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/ActivateCardModal', ActivateCardModal);